import React, { useMemo } from "react";

type LineBlock =
  | { type: "paragraph"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "blockquote"; lines: string[] };

function parseInline(text: string): React.ReactNode[] {
  // Match **bold**, __bold__, *italic*, _italic_
  // Bold must be checked before italic to avoid conflicts
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*(.+?)\*\*|__(.+?)__|\*(.+?)\*|_(.+?)_)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const boldContent = match[2] || match[3];
    const italicContent = match[4] || match[5];

    if (boldContent) {
      parts.push(
        <strong key={key++} className="font-bold text-app-accent">
          {boldContent}
        </strong>
      );
    } else if (italicContent) {
      parts.push(
        <em key={key++} className="italic text-app-info">
          {italicContent}
        </em>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

function parseBlocks(text: string): LineBlock[] {
  const lines = text.split("\n");
  const blocks: LineBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    // Skip empty lines
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Blockquote: > text
    if (/^>\s?/.test(line)) {
      const bqLines: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i]!)) {
        bqLines.push(lines[i]!.replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "blockquote", lines: bqLines });
      continue;
    }

    // Unordered list: - or *  (but not ** which is bold)
    if (/^-\s/.test(line) || /^\*\s/.test(line)) {
      const items: string[] = [];
      while (
        i < lines.length &&
        (/^-\s/.test(lines[i]!) || /^\*\s/.test(lines[i]!))
      ) {
        items.push(lines[i]!.replace(/^[-*]\s/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Ordered list: 1. 2. etc.
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^\d+\.\s/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Heading: strip # prefix, render as plain paragraph
    if (/^#{1,6}\s/.test(line)) {
      const headingText = line.replace(/^#{1,6}\s/, "");
      blocks.push({ type: "paragraph", text: headingText });
      i++;
      continue;
    }

    // Regular paragraph
    blocks.push({ type: "paragraph", text: line });
    i++;
  }

  return blocks;
}

/** Split text at the first standalone "--" line. Returns [before, after | undefined]. */
export function splitAtMarker(text: string): [string, string | undefined] {
  const idx = text.split("\n").findIndex((l) => l.trim() === "--");
  if (idx === -1) return [text, undefined];
  const lines = text.split("\n");
  return [lines.slice(0, idx).join("\n"), lines.slice(idx + 1).join("\n")];
}

function stripMarkdown(text: string): string {
  return text
    .split("\n")
    .map((line) =>
      line
        .replace(/^#{1,6}\s/, "")
        .replace(/^>\s?/, "")
        .replace(/^[-*]\s/, "")
        .replace(/^\d+\.\s/, "")
    )
    .join(" ")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

interface MarkdownTextProps {
  text: string;
  className?: string;
  plain?: boolean;
  /** When true, only show content before the first "--" line */
  truncateAtSplit?: boolean;
}

export const MarkdownText = React.memo(function MarkdownText({
  text,
  className = "",
  plain = false,
  truncateAtSplit = false,
}: MarkdownTextProps) {
  const rendered = useMemo(() => {
    if (!text) return null;

    const displayText = truncateAtSplit ? splitAtMarker(text)[0] : text;

    if (plain) return stripMarkdown(displayText);

    const blocks = parseBlocks(displayText);

    return blocks.map((block, idx) => {
      switch (block.type) {
        case "ul":
          return (
            <ul key={idx} className="list-disc pl-4 space-y-0.5">
              {block.items.map((item, j) => (
                <li key={j}>{parseInline(item)}</li>
              ))}
            </ul>
          );
        case "ol":
          return (
            <ol key={idx} className="list-decimal pl-4 space-y-0.5">
              {block.items.map((item, j) => (
                <li key={j}>{parseInline(item)}</li>
              ))}
            </ol>
          );
        case "blockquote":
          return (
            <blockquote
              key={idx}
              className="border-l-2 border-app-primary pl-2 italic opacity-80"
            >
              {block.lines.map((line, j) => (
                <p key={j}>{parseInline(line)}</p>
              ))}
            </blockquote>
          );
        case "paragraph":
          return <p key={idx}>{parseInline(block.text)}</p>;
      }
    });
  }, [text, plain, truncateAtSplit]);

  return <div className={className}>{rendered}</div>;
});
